import { ButtonWithLoading } from "@/components/shared/buttons/ButtonWithLoading";
import { ErrorModal } from "@/components/shared/modal/error-modal";
import {
	LadleTapTempAlertConfiguration,
	ladlesRepository,
} from "@/repositories/roots/ladlesRepository";
import {
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Divider,
	FormControlLabel,
	Grid,
	IconButton,
	Paper,
	Snackbar,
	TextField,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { Alert } from "@material-ui/lab";
import { useEffect, useState } from "react";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const splitEmails = (value: string | null): string[] =>
	(value ?? "")
		.split(";")
		.map((email) => email.trim())
		.filter((email) => email.length > 0);

const getErrorMessage = (e: any): string =>
	e?.response?.data?.message ??
	e?.response?.data?.detail ??
	e?.message ??
	String(e);

const DEFAULT_CONFIG: LadleTapTempAlertConfiguration = {
	ladleTapTempAlertConfigurationId: 0,
	alertCode: "TapTemperatureHigh",
	threshold: 0,
	isActive: false,
	sendEmailNotifications: false,
	notificationEmails: null,
	operatorRequired: true,
	commentsEnabled: true,
	updateTimestamp: null,
	updatedBy: null,
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			maxWidth: 600,
			margin: theme.spacing(3),
			padding: theme.spacing(3),
			display: "flex",
			flexDirection: "column",
		},
		header: {
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: theme.spacing(2),
			marginBottom: theme.spacing(2),
		},
		activeChip: {
			backgroundColor: theme.palette.success.main,
			color: "#fff",
		},
		emailsDisabled: {
			opacity: 0.6,
		},
		noAutofill: {
			"& input:-webkit-autofill": {
				WebkitBoxShadow: "0 0 0 1000px #fff inset",
				WebkitTextFillColor: theme.palette.text.primary,
				transition: "background-color 9999s ease-in-out 0s",
			},
		},
		divider: {
			margin: theme.spacing(2, 0),
		},
		sectionTitle: {
			color: theme.palette.text.secondary,
			fontWeight: 600,
			marginBottom: theme.spacing(1),
		},
		emailRow: {
			display: "flex",
			alignItems: "flex-start",
			gap: theme.spacing(1),
		},
		emptyHint: {
			color: theme.palette.text.secondary,
			fontStyle: "italic",
		},
		footer: {
			display: "flex",
			flexDirection: "column",
			alignItems: "stretch",
			gap: theme.spacing(1),
		},
		footerMeta: {
			color: theme.palette.text.secondary,
			alignSelf: "flex-end",
		},
		loadingRoot: {
			maxWidth: 600,
			margin: theme.spacing(3),
			padding: theme.spacing(6),
			display: "flex",
			justifyContent: "center",
		},
	}),
);

type Feedback = { type: "success" | "error"; text: string };

const LadleTapTempAlertPage = () => {
	const classes = useStyles();
	const [config, setConfig] = useState<LadleTapTempAlertConfiguration | null>(
		null,
	);
	const [loading, setLoading] = useState(true);
	const [emails, setEmails] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [errorModal, setErrorModal] = useState<string | null>(null);

	useEffect(() => {
		(async () => {
			try {
				const data = await ladlesRepository.getTapTempAlertConfiguration();
				const resolved =
					data && typeof data === "object" ? data : DEFAULT_CONFIG;
				setConfig(resolved);
				setEmails(splitEmails(resolved.notificationEmails));
			} catch (e: any) {
				setConfig(DEFAULT_CONFIG);
				setEmails([]);
				setErrorModal(getErrorMessage(e));
			} finally {
				setLoading(false);
			}
		})();
	}, []);

	const updateField = (
		field: keyof LadleTapTempAlertConfiguration,
		value: any,
	) => {
		setConfig((current) =>
			current ? { ...current, [field]: value } : current,
		);
	};

	const updateEmail = (index: number, value: string) => {
		setEmails((current) =>
			current.map((email, i) => (i === index ? value : email)),
		);
	};

	const addEmail = () => setEmails((current) => [...current, ""]);

	const removeEmail = (index: number) =>
		setEmails((current) => current.filter((_, i) => i !== index));

	const thresholdInvalid = config != null && Number(config.threshold) < 0;
	const isThresholdActive = config != null && Number(config.threshold) > 0;

	const isEmailInvalid = (email: string) => {
		const trimmed = email.trim();
		return trimmed.length > 0 && !EMAIL_REGEX.test(trimmed);
	};

	const save = async () => {
		if (!config) return;

		if (thresholdInvalid) return;

		const cleanedEmails = emails
			.map((email) => email.trim())
			.filter((email) => email.length > 0);

		if (config.sendEmailNotifications) {
			const hasInvalidEmail = cleanedEmails.some(
				(email) => !EMAIL_REGEX.test(email),
			);
			if (hasInvalidEmail) return;
		}

		setIsSaving(true);
		setFeedback(null);
		try {
			const updated = await ladlesRepository.updateTapTempAlertConfiguration({
				threshold: Number(config.threshold),
				isActive: Number(config.threshold) > 0,
				sendEmailNotifications: config.sendEmailNotifications,
				notificationEmails:
					cleanedEmails.length > 0 ? cleanedEmails.join(";") : null,
				operatorRequired: config.operatorRequired,
				commentsEnabled: config.commentsEnabled,
			});
			setConfig(updated);
			setEmails(splitEmails(updated.notificationEmails));
			setFeedback({ type: "success", text: "Configuration saved" });
		} catch (e: any) {
			setErrorModal(getErrorMessage(e));
		} finally {
			setIsSaving(false);
		}
	};

	if (loading || !config) {
		return (
			<>
				<Paper className={classes.loadingRoot}>
					<CircularProgress />
				</Paper>
				<ErrorModal error={errorModal} onHide={() => setErrorModal(null)} />
			</>
		);
	}

	const alertInactive = !isThresholdActive;
	const emailsDisabled = alertInactive || !config.sendEmailNotifications;

	return (
		<Paper className={classes.root}>
			<div className={classes.header}>
				<Typography variant="h5">Ladle Tap Temp Alert</Typography>
				{isThresholdActive ? (
					<Chip label="Active" className={classes.activeChip} />
				) : (
					<Chip label="Inactive" />
				)}
			</div>

			<Typography variant="subtitle2" className={classes.sectionTitle}>
				Alert threshold
			</Typography>
			<Grid container spacing={2} direction="column">
				<Grid item>
					<TextField
						label="Tap temperature threshold"
						type="number"
						value={config.threshold}
						onChange={(event) => updateField("threshold", event.target.value)}
						onFocus={(event) => event.target.select()}
						error={thresholdInvalid}
						helperText={
							thresholdInvalid
								? "Threshold cannot be negative."
								: isThresholdActive
								  ? "Alerts trigger when tap temperature ≥ this value."
								  : "Inactive. Set a value > 0 to activate."
						}
						fullWidth
						variant="outlined"
					/>
				</Grid>
			</Grid>

			<Typography variant="subtitle2" className={classes.sectionTitle}>
				Email notifications
			</Typography>
			<Grid container spacing={2} direction="column">
				<Grid
					item
					className={alertInactive ? classes.emailsDisabled : undefined}
				>
					<FormControlLabel
						control={
							<Checkbox
								color="primary"
								checked={config.sendEmailNotifications}
								disabled={alertInactive}
								onChange={(event) => {
									updateField("sendEmailNotifications", event.target.checked);
									setEmails((current) =>
										current.filter((email) => email.trim().length > 0),
									);
								}}
							/>
						}
						label="Send email notifications"
					/>
				</Grid>
				{emails.length === 0 && (
					<Grid item>
						<Typography variant="caption" className={classes.emptyHint}>
							No recipients yet. Add at least one email address.
						</Typography>
					</Grid>
				)}
				{emails.map((email, index) => {
					const invalid = isEmailInvalid(email);
					return (
						<Grid
							item
							key={index}
							className={emailsDisabled ? classes.emailsDisabled : undefined}
						>
							<div className={classes.emailRow}>
								<TextField
									label={`Email ${index + 1}`}
									type="email"
									value={email}
									onChange={(event) => updateEmail(index, event.target.value)}
									error={invalid}
									helperText={invalid ? "Invalid email address." : undefined}
									disabled={emailsDisabled}
									autoComplete="off"
									InputProps={{ className: classes.noAutofill }}
									fullWidth
									variant="outlined"
								/>
								<IconButton
									aria-label="Remove email"
									onClick={() => removeEmail(index)}
									disabled={emailsDisabled}
								>
									<DeleteOutlineIcon />
								</IconButton>
							</div>
						</Grid>
					);
				})}
				<Grid item>
					<Button
						color="primary"
						startIcon={<AddCircleOutlineIcon />}
						onClick={addEmail}
						disabled={emailsDisabled}
					>
						Add email
					</Button>
				</Grid>
			</Grid>

			<Divider className={classes.divider} />

			<Typography variant="subtitle2" className={classes.sectionTitle}>
				Acknowledge popup
			</Typography>
			<Grid container spacing={2} direction="column">
				<Grid item>
					<FormControlLabel
						control={
							<Checkbox
								color="primary"
								checked={config.operatorRequired}
								onChange={(event) =>
									updateField("operatorRequired", event.target.checked)
								}
							/>
						}
						label="Require operator to acknowledge"
					/>
				</Grid>
				<Grid item>
					<FormControlLabel
						control={
							<Checkbox
								color="primary"
								checked={config.commentsEnabled}
								onChange={(event) =>
									updateField("commentsEnabled", event.target.checked)
								}
							/>
						}
						label="Enable comments field"
					/>
				</Grid>
			</Grid>

			<Divider className={classes.divider} />

			<div className={classes.footer}>
				<ButtonWithLoading
					color="primary"
					variant="contained"
					onClick={save}
					isLoading={isSaving}
					fullWidth
				>
					Save
				</ButtonWithLoading>
				{config.updatedBy && (
					<Typography variant="caption" className={classes.footerMeta}>
						Last updated by {config.updatedBy}
					</Typography>
				)}
			</div>

			<Snackbar
				open={!!feedback}
				autoHideDuration={3000}
				onClose={() => setFeedback(null)}
			>
				{feedback ? (
					<Alert severity={feedback.type} onClose={() => setFeedback(null)}>
						{feedback.text}
					</Alert>
				) : undefined}
			</Snackbar>

			<ErrorModal error={errorModal} onHide={() => setErrorModal(null)} />
		</Paper>
	);
};

export default LadleTapTempAlertPage;
